<!--
 * @FileDescription: 优惠券管理页面
 * @Author: 陈文景
 * @Date: 2023-03-09 09:35:39
 * @LastEditors: 最后更新作者
 * @LastEditTime: 最后更新时间
-->
<template>
  <PageSkeletonTwo>
    <template slot="header-left">
      <el-tabs v-model="active" @tab-click="onTabClick">
        <el-tab-pane label="全部" name="all" />
        <el-tab-pane label="进行中" name="conduct" />
        <el-tab-pane label="已结束" name="over" />
      </el-tabs>
    </template>

    <template slot="condition">
      <el-input v-model="parameter.searchContent" clearable size="medium" placeholder="优惠券ID/名称" @clear="getCouponsList">
        <el-button slot="append" icon="el-icon-search" @click="getCouponsList" />
      </el-input>
      <el-button size="medium" type="primary" @click="onCreateCoupon">创建优惠券</el-button>
    </template>

    <template slot="container">
      <el-table ref="table" v-loading="loading" :data="coupons" row-key="id" :style="{ width: `${tableWidth}%` }" :height="`${tableHeight}%`" style="width: 100%">
        <el-table-column label="优惠券ID" prop="id" show-overflow-tooltip width="150" fixed="left" />
        <el-table-column prop="couponTicketName" show-overflow-tooltip fixed="left">
          <template slot="header">
            <span class="coupon-ticket-name-header" title="优惠券名称">优惠券名称</span>
          </template>
        </el-table-column>
        <el-table-column show-overflow-tooltip>
          <template slot="header">
            <span class="description-header" title="优惠券描述">优惠券描述</span>
          </template>
          <template v-slot="{ row }">
            {{ defaultTips(row.description) }}
          </template>
        </el-table-column>
        <el-table-column label="适用渠道">
          <template v-slot="{ row }">
            {{ getChannel(row.channel) }}
          </template>
        </el-table-column>
        <el-table-column label="优惠规则" show-overflow-tooltip>
          <template v-slot="{ row }">
            {{ getReceiveUsageRules(row.receiveUsageRules) }}
          </template>
        </el-table-column>
        <el-table-column label="状态" align="center" width="150">
          <template v-slot="{ row }">
            {{ getState(row.status) }}
          </template>
        </el-table-column>
        <el-table-column label="已领取(张)" align="center" width="150">
          <template v-slot="{ row }">
            <a href="javascript:;" class="received" @click="() => onReceiveQuantity(row)">{{ row.pickUpQuantity }}</a>
          </template>
        </el-table-column>
        <el-table-column label="剩余(张)" prop="remainderUpQuantity" align="center" width="150" />
        <el-table-column label="已使用(张)" prop="useQuantity" align="center" width="150" />
        <el-table-column fixed="right" :width="setOperateTableColumnWidth">
          <template slot="header">
            <span class="operate-header">操作</span>
          </template>
          <template v-slot="{ row }">
            <div :style="{ 'text-align': active === 'over' ? 'center' : 'start' }">
              <a v-if="row.status === 0" href="javascript:;" class="check" @click="() => onCheck(row)">查看</a>
              <span v-if="row.status === 0" class="line">|</span>
              <a v-if="row.status === 1" href="javascript:;" class="edit" @click="() => onEdit(row)">编辑</a>
              <span v-if="row.status === 1" class="line">|</span>
              <a v-if="row.status === 1" href="javascript:;" class="finish" @click="() => onFinish(row)">结束</a>
              <span v-if="row.status === 1" class="line">|</span>
              <a v-if="row.status === 1 && (row.remainderUpQuantity > 0 || row.remainderUpQuantity === '不限')" href="javascript:;" class="distribute" @click="() => onDistribute(row)">手动发放</a>
              <span v-if="row.status === 1 && (row.remainderUpQuantity > 0 || row.remainderUpQuantity === '不限')" class="line">|</span>
              <a href="javascript:;" class="data" @click="() => onData(row)">数据</a>
              <span class="line">|</span>
              <a href="javascript:;" class="copy" @click="() => onCopy(row)">复制</a>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template #footer-right>
      <el-pagination background :current-page="parameter.pageNumber" :page-sizes="[20, 50, 100, 200, 500]" :page-size="parameter.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="total" class="el-pagination" @size-change="onSizeChange" @current-change="onCurrentChange" />
    </template>

    <!-- 手动发货对话框 -->
    <ManualReleaseDialog ref="ManualReleaseDialog" @update="getCouponsList" />
  </PageSkeletonTwo>
</template>

<script>
import API from '@/api'
import PageSkeletonTwo from './components/PageSkeletonTwo.vue'
import ManualReleaseDialog from './components/ManualReleaseDialog.vue'

let parameter = {
  status: undefined, // 优惠券状态(全部时不传)：0.已结束 1.正在进行
  searchContent: undefined, // 搜索项(优惠券名称或优惠码ID)
  pageNumber: 1,
  pageSize: 20
}

export default {
  name: 'operation_coupon',
  components: {
    PageSkeletonTwo,
    ManualReleaseDialog
  },
  data() {
    return {
      active: 'all',

      tableWidth: 100,
      tableHeight: 100,

      parameter: { ...parameter }, // 获取优惠券列表的请求参数

      coupons: [], // 优惠券列表
      total: 0, // 总数
      loading: false // 表格加载效果
    }
  },
  computed: {
    setOperateTableColumnWidth() {
      if (this.active === 'over') {
        return 158
      } else {
        return 250
      }
    },
    getChannel() {
      return (value) => {
        if (value && value.length) {
          return value.join('、')
        } else {
          return '-'
        }
      }
    },
    /**
     * 优惠规则
     */
    getReceiveUsageRules() {
      return (rules) => {
        return this.getConditionsOfUse(rules.conditionsOfUse || {}, rules.offerTypeInfo || {}, rules.applicableProducts || {})
      }
    },
    getConditionsOfUse() {
      return (conditionsOfUse, offerTypeInfo, applicableProducts) => {
        switch (conditionsOfUse.useType) {
          case 1:
            return `无门槛，${this.getOfferTypeInfo(offerTypeInfo)}，${this.getApplicableType(applicableProducts.applicableType)}`
          case 2:
            return `满${this.defaultTips(conditionsOfUse.threshold)}USD，${this.getOfferTypeInfo(offerTypeInfo)}，${this.getApplicableType(applicableProducts.applicableType)}`
          case 3:
            return `满${this.defaultTips(conditionsOfUse.threshold)}件，${this.getOfferTypeInfo(offerTypeInfo)}，${this.getApplicableType(applicableProducts.applicableType)}`
          default:
            return '-'
        }
      }
    },
    getOfferTypeInfo() {
      return (offerTypeInfo) => {
        if (offerTypeInfo.fixedType) {
          switch (offerTypeInfo.fixedType) {
            case 1:
              return `优惠${offerTypeInfo.reduction}USD`
            case 2:
              return `优惠${offerTypeInfo.reduction}%`
            default:
              return '-'
          }
        } else {
          return '-'
        }
      }
    },
    getApplicableType() {
      return (type) => {
        switch (type) {
          case 1:
            return '全部商品可用'
          case 2:
            return '非活动商品可用'
          case 3:
            return '指定商品可用'
          default:
            return '-'
        }
      }
    },
    getState() {
      return (type) => {
        switch (type) {
          case 0:
            return '已结束'
          case 1:
            return '进行中'
          default:
            return '-'
        }
      }
    },
    defaultTips() {
      return (value) => value || '-'
    }
  },
  created() {
    this.getCouponsList()
  },
  methods: {
    /**
     * API 获取优惠券列表
     *
     * @param {Boolean} reset 是否重置分页
     */
    async getCouponsList(reset = true) {
      try {
        if (reset) {
          this.parameter.pageNumber = 1
        }
        this.loading = true
        let query = { ...this.parameter }
        if (query.searchContent) {
          query.searchContent = query.searchContent.trim()
        }
        let { data } = await API.getCouponsList(query)
        this.total = data.total
        this.coupons = data.list
        this.loading = false
      } catch (error) {
        this.loading = false
      }
    },

    /**
     * API 更新优惠券状态
     */
    async updateCouponStatus(query) {
      let { code, message } = await API.updateCouponStatus(query)
      if (code === 10000) {
        this.getCouponsList(false)
        this.$message.success(message)
      } else {
        this.$message.error(message)
      }
    },

    /**
     * 切换Tabs的事件回调
     */
    onTabClick() {
      switch (this.active) {
        case 'all':
          this.parameter.status = undefined
          break
        case 'conduct':
          this.parameter.status = 1
          break
        case 'over':
          this.parameter.status = 0
          break
      }
      this.getCouponsList()
      // 重排表格
      this.$refs.table.doLayout()
    },

    /**
     * 创建优惠券
     */
    onCreateCoupon() {
      this.$store.dispatch('coupon/setCouponInfo', { operateType: 1, couponId: undefined })
      this.$router.push('/operation/coupon/couponInfo')
    },

    /**
     * 点击已领取(张)的回调事件
     *
     * @param {Object} row 当前行数据
     */
    onReceiveQuantity(row) {
      this.$router.push({
        path: '/operation/customer',
        query: {
          couponName: row.id
        }
      })
    },

    /**
     * 查看
     *
     * @param {Object} row 当前行数据
     */
    onCheck(row) {
      this.$store.dispatch('coupon/setCouponInfo', { operateType: 2, couponId: row.id })
      this.$router.push('/operation/coupon/couponInfo')
    },

    /**
     * 编辑
     *
     * @param {Object} row 当前行数据
     */
    onEdit(row) {
      this.$store.dispatch('coupon/setCouponInfo', { operateType: 3, couponId: row.id })
      this.$router.push('/operation/coupon/couponInfo')
    },

    /**
     * 结束
     *
     * @param {Object} row 当前行数据
     */
    onFinish(row) {
      this.$confirm(
        `<div>
            <p style="margin-bottom: 5px;"><strong style="color: #99c41c;">提示</strong>：结束优惠券后，无法继续编辑优惠券的内容，是否继续？</p>
            <p><strong style="color: #f56c6c;">注意</strong>：客户将无法再领取该优惠券，但之前已领取的优惠券，在有效期内仍可继续使用。</p>
          </div>`,
        '确认消息',
        {
          showClose: false,
          customClass: 'message-box',
          closeOnClickModal: false,
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确认结束',
          cancelButtonText: '取消'
        }
      )
        .then(() => {
          this.updateCouponStatus({ id: row.id, status: 0 })
        })
        .catch(() => {
          this.$message.info('已放弃操作')
        })
    },

    /**
     * 手动发放
     *
     * @param {Object} row 当前行数据
     */
    onDistribute(row) {
      this.$refs.ManualReleaseDialog.show(row)
    },

    /**
     * 数据
     *
     * @param {Object} row 当前行数据
     */
    onData() {},

    /**
     * 复制
     *
     * @param {Object} row 当前行数据
     */
    onCopy(row) {
      this.$store.dispatch('coupon/setCouponInfo', { operateType: 4, couponId: row.id })
      this.$router.push('/operation/coupon/couponInfo')
    },

    /**
     * 每页显示数量发生改变时的回调
     *
     * @param size 每页显示数量
     */
    onSizeChange(size) {
      this.parameter.pageSize = size
      this.getCouponsList(false)
    },

    /**
     * 上一页/下一页/当前页发生改变时的回调
     *
     * @param current 当前页
     */
    onCurrentChange(current) {
      this.parameter.pageNumber = current
      this.getCouponsList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
a {
  color: #99c41c;
  text-decoration: none;
  outline: none;
  &:hover {
    text-decoration: underline;
  }
  &.received {
    color: #99c41c;
  }
  &.finish {
    color: #eb5757;
  }
  &.data {
    color: #dcdfe6;
    cursor: not-allowed;
  }
}

/deep/.el-tabs__header {
  margin: 0;
}

.el-input {
  width: 280px;
}

.description-header,
.coupon-ticket-name-header {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.operate-header {
  display: block;
  text-align: center;
}

.line {
  color: #dcdfe6;
  margin: 0 5px;
}
</style>
